import { Box, Flex, Grid, Text, VStack } from "@chakra-ui/react";
import { FaInstagram, FaXTwitter, FaYoutube } from "react-icons/fa6";
import type { GridProps, HTMLChakraProps } from "@chakra-ui/react";

import { CONTENT_MAX_WIDTH } from "ui/shared/layout/utils";
import type { CustomLinksGroup } from "types/footerLinks";
import { FaMedium } from "react-icons/fa";
import FooterLinkItem from "./FooterLinkItem";
import IconSvg from "ui/shared/IconSvg";
import IntTxsIndexingStatus from "./IntTxsIndexingStatus";
import { Link } from "toolkit/chakra/link";
import NetworkAddToWallet from "ui/shared/NetworkAddToWallet";
import React from "react";
import type { ResourceError } from "lib/api/resources";
import { Skeleton } from "toolkit/chakra/skeleton";
import { Tooltip } from "toolkit/chakra/tooltip";
import config from "configs/app";
import { copy } from "toolkit/utils/htmlEntities";
import getApiVersionUrl from "./utils/getApiVersionUrl";
import useApiQuery from "lib/api/useApiQuery";
import useFetch from "lib/hooks/useFetch";
import useIssueUrl from "lib/hooks/useIssueUrl";
import { useQuery } from "@tanstack/react-query";

const MAX_LINKS_COLUMNS = 4;

const FRONT_VERSION_URL = `https://github.com/blockscout/frontend/tree/${config.UI.footer.frontendVersion}`;
const FRONT_COMMIT_URL = `https://github.com/blockscout/frontend/commit/${config.UI.footer.frontendCommit}`;

const Footer = () => {
  const { data: backendVersionData } = useApiQuery(
    "general:config_backend_version",
    {
      queryOptions: {
        staleTime: Infinity,
        enabled: !config.features.opSuperchain.isEnabled,
      },
    },
  );
  const apiVersionUrl = getApiVersionUrl(backendVersionData?.backend_version);
  const issueUrl = useIssueUrl(backendVersionData?.backend_version);

  const FOOTER_LINKS = [
    {
      title: "Company",
      links: [
        {
          text: "Official WebSite",
          url: "https://dotone.online",
          external: true,
        },
        {
          text: "Contact Us",
          url: "https://docs.dotone.online/resources/contact-us",
          external: true,
        },
        {
          text: "Terms & Privacy",
          url: "https://docs.dotone.online/terms-and-policy",
          external: true,
        },
      ],
    },
    {
      title: "Products",
      links: [
        {
          text: "Bridge",
          url: "https://bridge.dotone.online",
          external: true,
        },
        { text: "Dex", url: "https://swap.dotone.online", external: true },
        {
          text: "Staking",
          url: "https://stake.dotone.online/",
          external: true,
        },
        {
          text: "Wizard",
          url: "https://wizard.dotone.online",
          external: true,
        },
        {
          text: "Multi Signature",
          url: "https://multisig.dotone.online",
          external: true,
        },
        {
          text: "MyDot",
          url: "https://mydot.one",
          external: true,
        },
        {
          text: "DotOne Cell",
          url: "https://dotonecell.ir",
          external: true,
        },
      ],
    },
    {
      title: "Documents",
      links: [
        {
          text: "Document",
          url: "https://docs.dotone.online",
          external: true,
        },
        {
          text: "EcoSystem",
          url: "https://docs.dotone.online/getting-started/ecosystem",
          external: true,
        },
        {
          text: "Validators",
          url: "https://docs.dotone.online/validator/overview",
          external: true,
        },
        {
          text: "API Documents",
          url: "https://docs.dotone.online/developers/api-reference",
          external: true,
        },
        {
          text: "WhitePaper",
          url: "https://docs.dotone.online/resources/whitepaper",
          external: true,
        },
        {
          text: "Network Status",
          url: "https://dotscan.one/stats",
          external: true,
        },
        {
          text: "GitHub",
          url: "https://github.com/dotOneSmartChain",
          external: true,
        },
      ],
    },
  ];

  const frontendLink = (() => {
    if (config.UI.footer.frontendVersion) {
      return (
        <Link href={FRONT_VERSION_URL} external noIcon>
          {config.UI.footer.frontendVersion}
        </Link>
      );
    }

    if (config.UI.footer.frontendCommit) {
      return (
        <Link href={FRONT_COMMIT_URL} external noIcon>
          {config.UI.footer.frontendCommit}
        </Link>
      );
    }

    return null;
  })();

  const fetch = useFetch();

  const { isPlaceholderData, data: linksData } = useQuery<
    unknown,
    ResourceError<unknown>,
    Array<CustomLinksGroup>
  >({
    queryKey: ["footer-links"],
    queryFn: async () =>
      fetch(config.UI.footer.links || "", undefined, {
        resource: "footer-links",
      }),
    enabled: Boolean(config.UI.footer.links),
    staleTime: Infinity,
    placeholderData: [],
  });

  const colNum = isPlaceholderData
    ? 1
    : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS) + 1;

  const renderNetworkInfo = React.useCallback(
    (gridArea?: GridProps["gridArea"]) => {
      return (
        <Flex
          gridArea={gridArea}
          flexWrap="wrap"
          columnGap={8}
          rowGap={6}
          mb={{ base: 5, lg: 10 }}
          _empty={{ display: "none" }}
          className="footer-network-actions"
        >
          {!config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus />}
          <NetworkAddToWallet />
        </Flex>
      );
    },
    [],
  );

  const renderProjectInfo = React.useCallback(
    (gridArea?: GridProps["gridArea"]) => {
      const logoColor = { base: "blue.600", _dark: "white" };
      const iconColor = { base: "blackAlpha.700", _dark: "whiteAlpha.700" };
      const iconHoverColor = { base: "blue.600", _dark: "blue.300" };
      const socialGroupLabelColor = { base: "black", _dark: "white" };

      return (
        <Box gridArea={gridArea}>
          {/* ✅ Social media icons */}
          <Flex mb={4} gap={4} alignItems="stretch">
            <VStack gap={1.5} alignItems="start">
              <Text
                fontSize="2xs"
                textTransform="uppercase"
                letterSpacing="wide"
                color={"white"}
                mx={"auto"}
              >
                Dotone Smart Chain
              </Text>
              <Flex gap={4} alignItems="center">
                <Tooltip content="Doto Youtube channel">
                  <Link
                    href="https://m.youtube.com/@DOTO_coin"
                    external
                    noIcon
                    aria-label="DotOne company on YouTube"
                    className="footer-social-link"
                    color={iconColor}
                    _hover={{ color: iconHoverColor }}
                  >
                    <FaYoutube size={24} />
                  </Link>
                </Tooltip>
                <Tooltip content="Doto medium channel">
                  <Link
                    href="https://medium.com/@dotone.online"
                    external
                    noIcon
                    aria-label="DotOne company on Medium"
                    className="footer-social-link"
                    color={iconColor}
                    _hover={{ color: iconHoverColor }}
                  >
                    <FaMedium size={24} />
                  </Link>
                </Tooltip>
                <Tooltip content="Doto X channel">
                  <Link
                    href="https://x.com/dotonenetwork"
                    external
                    noIcon
                    aria-label="DotOne company on X"
                    className="footer-social-link"
                    color={iconColor}
                    _hover={{ color: iconHoverColor }}
                  >
                    <FaXTwitter size={24} />
                  </Link>
                </Tooltip>
                <Tooltip content="Doto Instagram channel">
                  <Link
                    href="https://www.instagram.com/dotone_blockchain"
                    external
                    noIcon
                    aria-label="DotOne company on Instagram"
                    className="footer-social-link"
                    color={iconColor}
                    _hover={{ color: iconHoverColor }}
                  >
                    <FaInstagram size={24} />
                  </Link>
                </Tooltip>
              </Flex>
            </VStack>

            <Box alignSelf="stretch" w="1px" bg="blue.300" aria-hidden="true" />

            <VStack gap={1.5} alignItems="start">
              <Text
                fontSize="2xs"
                textTransform="uppercase"
                letterSpacing="wide"
                color={"white"}
              >
                Mydot
              </Text>
              <Flex gap={4} alignItems="center" h="24px" ml={1}>
                <Tooltip content="Mydot Instagram channel">
                  <Link
                    href="https://www.instagram.com/dotone_blockchain"
                    external
                    noIcon
                    aria-label="Mydot project on Instagram"
                    className="footer-social-link"
                    color={iconColor}
                    _hover={{ color: iconHoverColor }}
                  >
                    <FaInstagram size={24} />
                  </Link>
                </Tooltip>
              </Flex>
            </VStack>
          </Flex>

          <Text mt={3} fontSize="xs" className="footer-description">
            DotOneScan is a Block Explorer and Analytics Platform for the DotOne
            Smart Chain, a high-performance decentralized blockchain powering
            the DotOne Ecosystem.
          </Text>

          <Box mt={6} alignItems="start" textStyle="xs" className="footer-meta">
            {apiVersionUrl && (
              <Text>
                Backend:{" "}
                <Link href={apiVersionUrl} external noIcon>
                  {backendVersionData?.backend_version}
                </Link>
              </Text>
            )}
            {frontendLink && <Text>Frontend: {frontendLink}</Text>}
            <Text>
              Copyright {copy} DotOne Smart Chain Limited{" "}
              {new Date().getFullYear()}
            </Text>
          </Box>
        </Box>
      );
    },
    [apiVersionUrl, backendVersionData?.backend_version, frontendLink],
  );

  const containerProps: HTMLChakraProps<"div"> = {
    as: "footer",
    className: "site-footer",
    borderTopWidth: "1px",
    borderTopColor: "border.divider",
  };

  const contentProps: GridProps = {
    px: {
      base: 4,
      lg: config.UI.navigation.layout === "horizontal" ? 6 : 12,
      "2xl": 6,
    },
    py: { base: 4, lg: 8 },
    gridTemplateColumns: { base: "1fr", lg: "minmax(auto, 470px) 1fr" },
    columnGap: { lg: "32px", xl: "100px" },
    maxW: `${CONTENT_MAX_WIDTH}px`,
    m: "0 auto",
  };

  const renderRecaptcha = (gridArea?: GridProps["gridArea"]) => {
    if (!config.services.reCaptchaV2.siteKey) {
      return <Box gridArea={gridArea} />;
    }

    return (
      <Box gridArea={gridArea} textStyle="xs" mt={6}>
        <span>This site is protected by reCAPTCHA and the Google </span>
        <Link href="https://policies.google.com/privacy" external noIcon>
          Privacy Policy
        </Link>
        <span> and </span>
        <Link href="https://policies.google.com/terms" external noIcon>
          Terms of Service
        </Link>
        <span> apply.</span>
      </Box>
    );
  };

  // if (config.UI.footer.links) {
  //   return (
  //     <Box {...containerProps}>
  //       <Grid {...contentProps}>
  //         <div>
  //           {renderNetworkInfo()}
  //           {renderProjectInfo()}
  //           {renderRecaptcha()}
  //         </div>

  //         <Grid
  //           gap={{ base: 6, lg: 8, xl: 12 }}
  //           gridTemplateColumns={{
  //             base: "repeat(auto-fill, 160px)",
  //             lg: `repeat(3, 160px)`,
  //             xl: `repeat(3, 160px)`,
  //           }}
  //           justifyContent={{ lg: "flex-end" }}
  //           mt={{ base: 8, lg: 0 }}
  //         >
  //           {FOOTER_LINKS.map((group) => (
  //             <Box key={group.title}>
  //               <Text fontWeight={500} mb={3}>
  //                 {group.title}
  //               </Text>
  //               <VStack gap={1} alignItems="start">
  //                 {group.links.map((link) => (
  //                   <Link
  //                     key={link.text}
  //                     href={link.url}
  //                     external={link.external}
  //                     noIcon
  //                     color="white"
  //                     className="footer-links"
  //                     fontSize="sm"
  //                   >
  //                     {link.text}
  //                   </Link>
  //                 ))}
  //               </VStack>
  //             </Box>
  //           ))}
  //         </Grid>
  //       </Grid>
  //     </Box>
  //   );
  // }

  // return (
  //   <Box {...containerProps}>
  //     <Grid
  //       {...contentProps}
  //       gridTemplateAreas={{
  //         lg: `
  //         "network links-top"
  //         "info links-bottom"
  //         "recaptcha links-bottom"
  //       `,
  //       }}
  //     >
  //       {renderNetworkInfo({ lg: "network" })}
  //       {renderProjectInfo({ lg: "info" })}
  //       {renderRecaptcha({ lg: "recaptcha" })}

  //       <Grid
  //         gridArea={{ lg: "links-bottom" }}
  //         gap={1}
  //         gridTemplateColumns={{
  //           base: "repeat(auto-fill, 160px)",
  //           lg: "repeat(2, 160px)",
  //           xl: "repeat(3, 160px)",
  //         }}
  //         gridTemplateRows={{
  //           base: "auto",
  //           lg: "repeat(3, auto)",
  //           xl: "repeat(2, auto)",
  //         }}
  //         gridAutoFlow={{ base: "row", lg: "column" }}
  //         alignContent="start"
  //         justifyContent={{ lg: "flex-end" }}
  //         mt={{ base: 8, lg: 0 }}
  //       >
  //         {BLOCKSCOUT_LINKS.map((link) => (
  //           <FooterLinkItem {...link} key={link.text} />
  //         ))}
  //       </Grid>
  //     </Grid>
  //   </Box>
  // );
  return (
    <Box {...containerProps}>
      <Grid {...contentProps}>
        <div>
          {renderNetworkInfo()}
          {renderProjectInfo()}
          {renderRecaptcha()}
        </div>

        <Grid
          gap={{ base: 6, lg: 8, xl: 12 }}
          gridTemplateColumns={{
            base: "repeat(auto-fill, 160px)",
            lg: `repeat(3, 160px)`,
            xl: `repeat(3, 160px)`,
          }}
          justifyContent={{ lg: "flex-end" }}
          mt={{ base: 8, lg: 0 }}
        >
          {FOOTER_LINKS.map((group) => (
            <Box key={group.title} className="footer-link-container">
              <Text
                fontWeight={500}
                mb={3}
                color="blue.300"
                className="footer-group-title"
              >
                {group.title}
              </Text>
              <VStack gap={1} alignItems="start">
                {group.links.map((link) => (
                  <Link
                    key={link.text}
                    href={link.url}
                    external={link.external}
                    noIcon
                    color="white"
                    className="footer-links relative flex! gap-1"
                    fontSize="sm"
                  >
                    {link.text}
                  </Link>
                ))}
              </VStack>
            </Box>
          ))}
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(Footer);
